import { plain } from '../../lib/labels';
import { useEffect, useState, type ReactNode } from 'react';
import {
  App,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Alert,
  type TableColumnsType,
} from 'antd';
import {
  PlusOutlined,
  KeyOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EditOutlined,
  CopyOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth, type AdminRole } from '../../auth/store';
import { Can } from '../../rbac/RequirePermission';
import { PermissionEditor } from '../../rbac/PermissionEditor';

interface Operator {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  isActive: boolean;
  disabledAt: string | null;
  createdAt: string;
  updatedAt: string;
  twoFactorEnrolled: boolean;
  /** Explicitly granted keys — what the checkboxes are bound to. */
  grantedPermissions: string[];
  /** Role template ∪ grants — what the API will actually allow. */
  effectivePermissions: string[];
  /** True when the role has no template, so the boxes ARE the access. */
  grantOnlyRole: boolean;
}

const ROLE_COLOR: Record<AdminRole, string> = {
  super_admin: 'magenta',
  sub_admin: 'green',
  ops: 'blue',
  finance: 'gold',
  moderator: 'purple',
  kyc_reviewer: 'cyan',
  kyc_viewer: 'default',
  agency: 'orange',
};

// Per-role one-line description rendered inside each Select option so the
// super_admin doesn't have to memorise what each role can do at create time.
const ROLE_DESCRIPTIONS: Record<AdminRole, string> = {
  super_admin: 'Full access — money, KYC, moderation, admins, credentials',
  sub_admin: 'No access by default — you tick exactly what they can see and do',
  ops: 'KYC review, listener approval, calls, moderation, content',
  finance: 'Wallet adjust, refunds, payouts, TDS/GST reports',
  moderator: 'Moderation queue + force-end calls only',
  kyc_reviewer: 'KYC specialist — view, approve, reject. Nothing else.',
  kyc_viewer: 'Read-only KYC auditor — view docs, no decisions',
  agency: 'Agency partner — its own agency portal only (create it from Agencies)',
};

const ROLE_OPTIONS: Array<{ value: AdminRole; label: ReactNode }> = (
  // Agency logins are created from the Agencies page, linked to their agency.
  (Object.keys(ROLE_DESCRIPTIONS) as AdminRole[]).filter((r) => r !== 'agency')
).map((r) => ({
  value: r,
  label: (
    <div style={{ lineHeight: 1.25 }}>
      <div style={{ fontWeight: 600 }}>{r}</div>
      <div style={{ fontSize: 11, color: '#8B8B8B' }}>{ROLE_DESCRIPTIONS[r]}</div>
    </div>
  ),
}));

interface ApiError {
  response?: { data?: { code?: string; message?: string } };
}

/** Map a server error code/message to UI copy. */
function errMessage(e: unknown, fallback: string): string {
  const err = e as ApiError;
  return err.response?.data?.message ?? fallback;
}

export function AdminOperatorsTable() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const currentAdminId = useAuth((s) => s.profile?.id);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Operator | null>(null);
  /** The operator whose permission tree is open in the drawer. */
  const [permsFor, setPermsFor] = useState<Operator | null>(null);
  const [credentialReveal, setCredentialReveal] = useState<{
    email: string;
    password: string;
    purpose: 'created' | 'reset';
  } | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'operators'],
    queryFn: async () => (await api.get<Operator[]>('/admin/operators')).data,
    refetchInterval: 30_000,
  });

  const create = useMutation({
    mutationFn: async (input: {
      email: string;
      name: string;
      role: AdminRole;
      permissions?: string[];
    }) =>
      (await api.post<{ admin: Operator; generatedPassword: string }>(
        '/admin/operators',
        input,
      )).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'operators'] });
      setCreateOpen(false);
      setCredentialReveal({
        email: data.admin.email,
        password: data.generatedPassword,
        purpose: 'created',
      });
    },
    onError: (e) => {
      message.error(errMessage(e, 'Could not create admin operator'));
    },
  });

  const update = useMutation({
    mutationFn: async (vars: { id: string; name: string; role: AdminRole }) =>
      (await api.patch(`/admin/operators/${vars.id}`, { name: vars.name, role: vars.role })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'operators'] });
      setEditing(null);
      message.success('Operator updated');
    },
    onError: (e) => {
      message.error(errMessage(e, 'Could not update operator'));
    },
  });

  const reset = useMutation({
    mutationFn: async (op: Operator) =>
      (await api.post<{ generatedPassword: string; selfReset: boolean }>(
        `/admin/operators/${op.id}/reset-password`,
      )).data,
    onSuccess: (data, op) => {
      setCredentialReveal({ email: op.email, password: data.generatedPassword, purpose: 'reset' });
      if (data.selfReset) {
        message.warning(
          'You reset your OWN password. Save it now — the next sign-in needs this new one.',
        );
      }
    },
    onError: (e) => {
      message.error(errMessage(e, 'Could not reset password'));
    },
  });

  // Replaces the whole grant set with the checkbox state. The API audits the
  // added/removed diff, and the change lands on the operator's next request.
  const savePermissions = useMutation({
    mutationFn: async (vars: { id: string; permissions: string[] }) =>
      (
        await api.put<{ added: string[]; removed: string[] }>(
          `/admin/operators/${vars.id}/permissions`,
          { permissions: vars.permissions },
        )
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'operators'] });
      setPermsFor(null);
      const n = data.added.length + data.removed.length;
      message.success(
        n === 0
          ? 'No permission changes'
          : `Permissions saved — +${data.added.length} / −${data.removed.length}. Applies on their next request.`,
      );
    },
    onError: (e) => {
      message.error(errMessage(e, 'Could not save permissions'));
    },
  });

  const setDisabled = useMutation({
    mutationFn: async (vars: { id: string; disable: boolean }) =>
      (await api.post(`/admin/operators/${vars.id}/${vars.disable ? 'disable' : 'enable'}`)).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'operators'] });
      message.success(vars.disable ? 'Operator disabled' : 'Operator re-enabled');
    },
    onError: (e) => {
      message.error(errMessage(e, 'Could not update operator state'));
    },
  });

  const columns: TableColumnsType<Operator> = [
    {
      title: 'Email',
      dataIndex: 'email',
      render: (email: string, r) => (
        <Space size={6}>
          <span>{email}</span>
          {r.id === currentAdminId && (
            <Tag color="cyan" style={{ fontSize: 10 }}>
              you
            </Tag>
          )}
        </Space>
      ),
    },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Role',
      dataIndex: 'role',
      render: (r: AdminRole) => <Tag color={ROLE_COLOR[r]}>{plain(r)}</Tag>,
      width: 130,
    },
    {
      title: 'Two-step login',
      dataIndex: 'twoFactorEnrolled',
      render: (v: boolean) =>
        v ? <Tag color="success">enrolled</Tag> : <Tag color="default">off</Tag>,
      width: 110,
    },
    {
      title: 'Permissions',
      key: 'permissions',
      width: 150,
      render: (_: unknown, r: Operator) =>
        r.role === 'super_admin' ? (
          <Tooltip title="A super admin always has every permission and cannot be scoped.">
            <Tag color="magenta">all</Tag>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              r.grantedPermissions.length > 0
                ? `Granted: ${r.grantedPermissions.join(', ')}`
                : r.grantOnlyRole
                  ? 'No permissions ticked — this operator can sign in but reach nothing.'
                  : 'Nothing granted beyond the role template.'
            }
          >
            <Space size={4}>
              <Tag color={r.effectivePermissions.length > 0 ? 'blue' : 'error'}>
                {r.effectivePermissions.length} effective
              </Tag>
              {r.grantedPermissions.length > 0 && (
                <Tag color="green">+{r.grantedPermissions.length}</Tag>
              )}
            </Space>
          </Tooltip>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      render: (active: boolean) =>
        active ? <Tag color="success">active</Tag> : <Tag color="error">disabled</Tag>,
      width: 110,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleDateString(),
      width: 130,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 320,
      render: (_: unknown, r: Operator) => {
        const isSelf = r.id === currentAdminId;
        return (
          <Space size={4} wrap>
            <Can permission="operators.edit">
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(r)}>
                Edit
              </Button>
            </Can>
            <Can permission="operators.permissions.edit">
              <Tooltip
                title={
                  r.role === 'super_admin'
                    ? 'A super admin always has everything — change their role first to scope them.'
                    : undefined
                }
              >
                <Button
                  size="small"
                  icon={<SafetyCertificateOutlined />}
                  disabled={r.role === 'super_admin'}
                  onClick={() => setPermsFor(r)}
                >
                  Permissions
                </Button>
              </Tooltip>
            </Can>
            <Can permission="operators.reset_password"><Popconfirm
              title="Reset password?"
              description={
                isSelf
                  ? 'You will need the new password to sign in again. Save it carefully.'
                  : `A new password will be generated for ${r.email}. Shown to you ONCE.`
              }
              onConfirm={() => reset.mutate(r)}
              okText="Reset"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" icon={<KeyOutlined />}>
                Reset password
              </Button>
            </Popconfirm></Can>
            {r.isActive ? (
              <Can permission="operators.disable"><Popconfirm
                title="Disable this operator?"
                description="They can't sign in until re-enabled. Audit history is preserved."
                onConfirm={() => setDisabled.mutate({ id: r.id, disable: true })}
                okText="Disable"
                okButtonProps={{ danger: true }}
                disabled={isSelf}
              >
                <Button size="small" icon={<StopOutlined />} danger disabled={isSelf}>
                  Disable
                </Button>
              </Popconfirm></Can>
            ) : (
              <Can permission="operators.disable">
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  type="primary"
                  onClick={() => setDisabled.mutate({ id: r.id, disable: false })}
                >
                  Re-enable
                </Button>
              </Can>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Typography.Text strong>Admin operators</Typography.Text>
          <Can permission="operators.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
              size="small"
            >
              Add operator
            </Button>
          </Can>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        message="Passwords are generated by the server"
        description="On create or reset, the plaintext password is shown ONCE in a modal. There is no way to retrieve it after that — share it with the operator out-of-band and have them sign in immediately."
        style={{ marginBottom: 16 }}
      />

      <Table<Operator>
        rowKey="id"
        columns={columns}
        dataSource={list.data ?? []}
        loading={list.isLoading}
        pagination={false}
        size="small"
      />

      <CreateOperatorModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onSubmit={(v) => create.mutate(v)}
        submitting={create.isPending}
      />

      <EditOperatorModal
        operator={editing}
        onCancel={() => setEditing(null)}
        onSubmit={(v) => editing && update.mutate({ id: editing.id, ...v })}
        submitting={update.isPending}
      />

      <PermissionsDrawer
        operator={permsFor}
        onClose={() => setPermsFor(null)}
        onSave={(permissions) =>
          permsFor && savePermissions.mutate({ id: permsFor.id, permissions })
        }
        saving={savePermissions.isPending}
      />

      <CredentialRevealModal
        reveal={credentialReveal}
        onClose={() => setCredentialReveal(null)}
        onCopy={(text) => {
          void navigator.clipboard.writeText(text);
          message.success('Password copied to clipboard');
        }}
      />
    </Card>
  );
}

function CreateOperatorModal({
  open,
  onCancel,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onCancel: () => void;
  onSubmit: (v: {
    email: string;
    name: string;
    role: AdminRole;
    permissions?: string[];
  }) => void;
  submitting: boolean;
}) {
  const [form] = Form.useForm<{ email: string; name: string; role: AdminRole }>();
  // The boxes are part of CREATE so a sub admin is never left sitting with no
  // access waiting for a second trip through the Permissions drawer.
  const [permissions, setPermissions] = useState<string[]>([]);
  const role = (Form.useWatch('role', form) ?? 'sub_admin') as AdminRole;

  // Switching role changes which keys are already covered by the template, so
  // the ticks from the previous role would be meaningless. Start clean.
  useEffect(() => setPermissions([]), [role]);

  const reset = () => {
    form.resetFields();
    setPermissions([]);
  };
  return (
    <Modal
      title="Add admin operator"
      open={open}
      width={860}
      onCancel={() => {
        reset();
        onCancel();
      }}
      okText="Create"
      onOk={() => {
        form
          .validateFields()
          .then((v) => {
            onSubmit({ ...v, permissions });
            reset();
          })
          .catch(() => {
            /* validation handles its own UI */
          });
      }}
      confirmLoading={submitting}
    >
      <Alert
        type="warning"
        showIcon
        message="A 16-character password is generated by the server"
        description="It will be shown to you once after creation. Save it before closing the dialog."
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical" initialValues={{ role: 'sub_admin' as AdminRole }}>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Required' },
            { type: 'email', message: 'Enter a valid email' },
          ]}
        >
          <Input placeholder="newop@maja.in" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="name"
          label="Display name"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input placeholder="Riya Operator" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: 'Required' }]}
          extra="sub_admin starts with nothing and is exactly the boxes you tick below."
        >
          <Select options={ROLE_OPTIONS} />
        </Form.Item>
      </Form>
      <Typography.Title level={5} style={{ marginTop: 8 }}>
        Permissions
      </Typography.Title>
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        <PermissionEditor role={role} value={permissions} onChange={setPermissions} />
      </div>
    </Modal>
  );
}

/**
 * The permission checkbox tree for one existing operator. Submitted whole —
 * the API replaces their grant set with exactly what is ticked and audits the
 * added/removed diff.
 */
function PermissionsDrawer({
  operator,
  onClose,
  onSave,
  saving,
}: {
  operator: Operator | null;
  onClose: () => void;
  onSave: (permissions: string[]) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<string[]>([]);
  useEffect(() => {
    setDraft(operator?.grantedPermissions ?? []);
  }, [operator]);

  return (
    <Drawer
      width={760}
      open={!!operator}
      onClose={onClose}
      title={operator ? `Permissions — ${operator.email}` : 'Permissions'}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={() => onSave(draft)}>
            Save permissions
          </Button>
        </Space>
      }
    >
      {operator && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={`Role: ${operator.role}`}
            description="Saving replaces their granted permissions with exactly what is ticked. It takes effect on their next request — no re-login needed — and the change is recorded in the audit log with what was added and removed."
          />
          <PermissionEditor role={operator.role} value={draft} onChange={setDraft} />
        </Space>
      )}
    </Drawer>
  );
}

function EditOperatorModal({
  operator,
  onCancel,
  onSubmit,
  submitting,
}: {
  operator: Operator | null;
  onCancel: () => void;
  onSubmit: (v: { name: string; role: AdminRole }) => void;
  submitting: boolean;
}) {
  const currentAdminId = useAuth((s) => s.profile?.id);
  const [form] = Form.useForm<{ name: string; role: AdminRole }>();
  const isSelf = operator?.id === currentAdminId;
  return (
    <Modal
      title={operator ? `Edit ${operator.email}` : 'Edit operator'}
      open={!!operator}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      okText="Save"
      destroyOnClose
      onOk={() => {
        form
          .validateFields()
          .then(onSubmit)
          .catch(() => undefined);
      }}
      confirmLoading={submitting}
    >
      {operator && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: operator.name, role: operator.role }}
        >
          <Form.Item
            name="name"
            label="Display name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Required' }]}
            extra={
              isSelf
                ? 'You cannot change your own role — ask another super_admin.'
                : undefined
            }
          >
            <Select options={ROLE_OPTIONS} disabled={isSelf} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

function CredentialRevealModal({
  reveal,
  onClose,
  onCopy,
}: {
  reveal: { email: string; password: string; purpose: 'created' | 'reset' } | null;
  onClose: () => void;
  onCopy: (text: string) => void;
}) {
  return (
    <Modal
      title={
        reveal?.purpose === 'created'
          ? 'New operator created'
          : 'Password reset complete'
      }
      open={!!reveal}
      onCancel={onClose}
      onOk={onClose}
      okText="I've saved it"
      cancelButtonProps={{ style: { display: 'none' } }}
      closable={false}
      maskClosable={false}
    >
      {reveal && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="This is the only time this password is visible"
            description="The server does not store the plaintext — only the bcrypt hash. If you lose it, you'll have to reset again."
          />
          <Typography.Text strong>Email</Typography.Text>
          <Typography.Text code copyable={{ text: reveal.email }}>
            {reveal.email}
          </Typography.Text>
          <Typography.Text strong>Password</Typography.Text>
          <Input.Group compact>
            <Input
              value={reveal.password}
              readOnly
              style={{ width: 'calc(100% - 110px)', fontFamily: 'monospace', fontSize: 16 }}
            />
            <Button icon={<CopyOutlined />} onClick={() => onCopy(reveal.password)} style={{ width: 110 }}>
              Copy
            </Button>
          </Input.Group>
        </Space>
      )}
    </Modal>
  );
}
